import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { areEqual } from 'Utils/equalityChecks';

import { Icon } from 'Components/Icons';

import { PurpleButton } from 'Components/Button';
import { TimeTrackingProvider } from 'Context/TimeTracking';

import { TimeTrackingButtonNav } from 'Containers/Nav/TimeTrackingButtonNav';
import { ClockInModal } from 'Containers/TimeTracking';

import classes from './nav.module.css';

interface Props {
  userAvatar: any;
  firstName: string;
  timeTrackingEnabled: boolean;
  unreadCount: number;
  toggleSideBar: (e: any) => void;
  createProjectAction: (e: any) => void;
  toAccountOnClick: (e: any) => void;
  onSupportClick: (e: any) => void;
}

const Nav = ({
  userAvatar,
  firstName,
  timeTrackingEnabled,
  unreadCount,
  toggleSideBar,
  createProjectAction,
  toAccountOnClick,
  onSupportClick,
}: Props) => {
  const [helpMenuOpen, setHelpMenuOpen] = useState(false);
  const helpMenuRef = useRef<HTMLDivElement>(null);

  const toggleHelpMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setHelpMenuOpen((prev) => !prev);
  }, []);

  const handleConversationsClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setHelpMenuOpen(false);
      onSupportClick(e);
    },
    [onSupportClick]
  );

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (helpMenuRef.current && !helpMenuRef.current.contains(e.target as Node)) {
        setHelpMenuOpen(false);
      }
    };
    if (helpMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [helpMenuOpen]);

  return (
    <div className={classes.container}>
      <div className={classes.left}>
        <Icon className={classes.hamburgerMenu} type="hamburgerMenu" onClick={toggleSideBar} />
      </div>
      <div className={classes.right}>
        <div className={classes.buttonContainer}>
          <PurpleButton className={classes.createProjectButton} onClick={createProjectAction}>
            Create Project <Icon type="plus" />
          </PurpleButton>
        </div>

        <div className={classes.helpMenuWrapper} ref={helpMenuRef}>
          <button type="button" className={classes.helpMenuTrigger} onClick={toggleHelpMenu}>
            <Icon type="support" />
            {unreadCount > 0 && <span className={classes.unreadBadge}>{unreadCount > 9 ? '9+' : unreadCount}</span>}
          </button>
          {helpMenuOpen && (
            <div className={classes.helpMenuDropdown}>
              <button type="button" className={classes.helpMenuItem} onClick={handleConversationsClick}>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M4 4h12c1.1 0 2 .9 2 2v8c0 1.1-.9 2-2 2H7l-4 3V6c0-1.1.9-2 2-2z"
                    stroke="#4E4B66"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    fill="none"
                  />
                </svg>
                <span>Conversations</span>
                {unreadCount > 0 && <span className={classes.menuBadge}>{unreadCount}</span>}
              </button>
              <Link
                to="/help/rocketplan"
                className={classes.helpMenuItem}
                target="_blank"
                onClick={() => setHelpMenuOpen(false)}
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M3 3h9c1.1 0 2 .9 2 2v10c0 1.1-.9 2-2 2H3c-1.1 0-2-.9-2-2V5c0-1.1.9-2 2-2zm0 2v10h9V5H3zm11-2v14m-7-7h5m-5 2.5h3.5"
                    stroke="#4E4B66"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    fill="none"
                  />
                </svg>
                <span>Help Center</span>
              </Link>
            </div>
          )}
        </div>

        {timeTrackingEnabled && (
          <TimeTrackingProvider>
            <TimeTrackingButtonNav />
            <ClockInModal />
          </TimeTrackingProvider>
        )}

        <div className={classes.welcomeContainer}>
          <a className={classes.welcome} href="#" onClick={toAccountOnClick}>
            {' '}
            {/* href="#" onClick={logOutClick} */}
            Hi, <strong>{firstName}</strong>
          </a>
        </div>
        <div>
          <span
            className={classes.accountBox}
            onClick={toAccountOnClick}
            onKeyUp={toAccountOnClick}
            role="button"
            tabIndex={-1}
          >
            {userAvatar}
          </span>
        </div>
      </div>
    </div>
  );
};

const NavMemo = memo(Nav, areEqual);
export { NavMemo as Nav };
